import { Button, Col, Popconfirm, Row, Space, Tooltip } from "antd";
import { useState } from "react";
import Icon from "../../../assets/icons/icon";
import DividerLine from "../../../components/lineSplit";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import { useTaxLegalId } from "../../../contexts/taxLegalCaseStore";
import { INVOICE_SENT_ID, PAID_ID, TaxLegalReceivablesStore } from "../../../contexts/taxLegalReceivablesStore";
import _t from "../../../lang/translate";
import { markPaidReceivable, sendInvoiceReceivable } from "../../../services/taxLegalService";
import { Receivable, ReceivableId } from "../../../types/taxLegalTypes";
import format from "../../../utilities/formatNumbers";
import { openFileLink } from "../../../services/mediaService";

function getLoadingId(id: string | number, action: "markAccepted" | "markPaid" | "downloadInvoice" | "downloadOffer" | "sendInvoice") {
  if (action) return action + "_" + id
  return id
}
export default function InvoiceActions() {
  const { acceptedOffers, invoices, replaceReceivable } = TaxLegalReceivablesStore.useContainer()
  const taxLegalCaseId = useTaxLegalId()

  const totalOfferPrice = acceptedOffers?.reduce((total, offer) => total + (offer.totals?.withVat ?? 0), 0)

  const outstandingInvoices = invoices?.filter(item => item.status?.id === INVOICE_SENT_ID);
  const outstandingInvoiceValue = outstandingInvoices?.reduce((total, invoice) => total + (invoice?.totals?.withVat ?? 0), 0);

  const paidInvoices = invoices?.filter(item => item.status?.id === PAID_ID)
  const totalPaidInvoices = paidInvoices?.reduce((total, invoice) => total + (invoice?.totals?.withVat ?? 0), 0);

  const [loadingIds, setLoadingIds] = useState<Set<string | number>>(new Set())


  function toggleLoading(id: string | number, loading: boolean) {
    setLoadingIds((prev) => {
      const newSet = new Set(prev);
      if (loading) {
        newSet.add(id)
      } else {
        newSet.delete(id)
      }
      return newSet
    })
  }

  async function downloadInvoice(invoice: Receivable) {
    try {
      if (!invoice.links.invoicePdf) return;
      toggleLoading(getLoadingId(invoice.id, "downloadInvoice"), true)
      await openFileLink(invoice.links.invoicePdf)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(getLoadingId(invoice.id, "downloadInvoice"), false)
    }
  }


  // NOTE: 2026-05-27 (Nico): Disable this feature because the offerPdf no longer exists inside E-conomic once the receivable has been converted into an invoice.
  async function downloadOffer(invoice: Receivable) {
    try {
      if (!invoice.links.offerPdf) return;
      toggleLoading(getLoadingId(invoice.id, "downloadOffer"), true)
      await openFileLink(invoice.links.offerPdf)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(getLoadingId(invoice.id, "downloadOffer"), false)
    }
  }

  async function handleSendInvoice(offerId: ReceivableId) {
    try {
      toggleLoading(getLoadingId(offerId, "sendInvoice"), true)
      const { data } = await sendInvoiceReceivable(taxLegalCaseId, offerId)
      replaceReceivable(data)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(getLoadingId(offerId, "sendInvoice"), false)
    }
  }

  async function markInvoicePaid(id: ReceivableId) {
    try {
      toggleLoading(getLoadingId(id, "markPaid"), true)
      const { data } = await markPaidReceivable(taxLegalCaseId, id)
      replaceReceivable(data)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(getLoadingId(id, "markPaid"), false)
    }
  }

  return (
    <div className="legal-activity-offer-card">
      <Row justify="start" align="stretch" gutter={[12, 12]}>
        <Col flex={"1 1 0%"}>
          <div style={{ padding: "6px 10px" }} className="border-radius border-light height-100">
            <small>{_t("ready_for_invoice")}</small>
            <p className="mb-0 text-medium">{format.price(totalOfferPrice)}</p>
            <small>{acceptedOffers?.length} {_t("offer")}</small>
          </div>
        </Col>
        <Col flex={"1 1 0%"}>
          <div style={{ padding: "6px 10px" }} className="border-radius border-light height-100">
            <small>{_t("outstanding")}</small>
            <p className="mb-0 text-medium">{format.price(outstandingInvoiceValue)}</p>
            <small>{outstandingInvoices?.length} {_t("active")}</small>
          </div>
        </Col>
        <Col flex={"1 1 0%"}>
          <div style={{ padding: "6px 10px" }} className="border-radius border-light height-100">
            <small>{_t("paid", "inTotal")}</small>
            <p className="mb-0 text-medium">{format.price(totalPaidInvoices)}</p>
            <small>{paidInvoices?.length} {_t("paid")}</small>
          </div>
        </Col>
      </Row>
      <DividerLine />

      <Row justify="space-between">
        <Col>{_t("ready_for_invoicing")}</Col>
        <Col><small>{acceptedOffers?.length} {_t("offer")}</small></Col>
      </Row>

      {acceptedOffers?.map((offer) => {
        return (
          <div key={offer.id} className="p-05 mt-05 border-light border-radius bg-light">
            <Row justify="space-between" align="middle">
              <Col style={{ display: "flex", alignItems: "center" }}>
                <Icon name="attach-outline" size="large" className="text-primary" />
                <div>
                  <Button style={{ display: "block", fontWeight: 400 }}
                    type="link" size="small"
                    onClick={() => downloadOffer(offer)}
                    loading={loadingIds.has(getLoadingId(offer.id, "downloadOffer"))}
                  >
                    {offer.title}
                  </Button>
                  <small style={{ marginTop: -4, display: 'block', marginLeft: 8 }}>
                    {offer.log?.published?.person?.fullName} &mdash; {offer.itemLines?.length} linjer &mdash; {format.price(offer.totals?.withVat)}
                  </small>
                </div>
              </Col>
              <Col>
                <span style={{ borderRadius: 16, padding: "2px 12px", background: "#FFE7BA", color: "#FA8C16", fontWeight: 500, fontSize: 10 }}>
                  <Icon name="check-circle-bold" />{' '}
                  {_t("accepted")}
                </span>
              </Col>
            </Row>
            <DividerLine className="mt-05 mb-05" />
            <div className="text-right">
              {offer.generatingInvoice ? (
                <Tooltip title="Afventer Economic - fakturaen bliver genereret i Economic">
                  <Button size="small" loading icon={<Icon name="paper-plane-outline" />} >
                    {_t("msg.generating_invoice")}
                  </Button>
                </Tooltip>
              ) : (
                <Popconfirm onConfirm={() => handleSendInvoice(offer.id)} title={_t("msg.confirm_action")}>
                  <Button size="small" type="primary" icon={<Icon name="paper-plane-outline" />}
                    loading={loadingIds.has(getLoadingId(offer.id, "sendInvoice"))}
                  >
                    {_t("send", "invoice")}
                  </Button>
                </Popconfirm>
              )}
            </div>
          </div>
        )
      })}


      <Row className="mt-1" justify="space-between">
        <Col>{_t("active_invoices")}</Col>
        <Col><small>{invoices?.length} {_t("invoices")}</small></Col>
      </Row>

      {invoices?.toSorted((a, b) => (a.status?.id ?? 0) - (b.status?.id ?? 0))?.map((invoice) => {
        const paid = invoice.status?.id === PAID_ID;
        return (
          <div key={invoice.id} className="p-05 mt-05 border-light border-radius bg-light">
            <Row justify="space-between" align="middle">
              <Col style={{ display: "flex", alignItems: "center" }}>
                <Icon name="attach-outline" size="large" className="text-primary" />
                <div>
                  <Button style={{ display: "block", fontWeight: 400 }}
                    type="link" size="small"
                    onClick={() => downloadInvoice(invoice)}
                    disabled={!invoice.links.invoicePdf || loadingIds.has(getLoadingId(invoice.id, "downloadInvoice"))}
                  >
                    {invoice.title}
                  </Button>
                  {paid ? (
                    <>
                      <small style={{ marginTop: -4, display: 'block', marginLeft: 8 }}>
                        Markeret betalt af <span className="text-dark">{invoice.log?.paid?.person?.fullName}</span> den {format.date(invoice.log?.paid?.date)} &mdash; {format.price(invoice.totals?.withVat)}
                      </small>
                    </>
                  ) : (
                    <small style={{ marginTop: -4, display: 'block', marginLeft: 8 }}>
                      {invoice?.paymentTerms?.title}
                    </small>
                  )}
                </div>
              </Col>
              <Col>
                {paid ? (
                  <span style={{ borderRadius: 16, padding: "2px 12px", background: "#D9F7BE", color: "#52C41A", fontWeight: 500, fontSize: 10 }}>
                    <Icon name="check-circle-bold" />{' '}
                    {_t("paid")}
                  </span>
                ) : (
                  <Popconfirm onConfirm={() => markInvoicePaid(invoice.id)} title={_t("msg.confirm_action")}>
                    <Button
                      loading={loadingIds.has(getLoadingId(invoice.id, "markPaid"))}
                      className={`legal-offer-accept-btn`}
                      size="small" icon={<Icon name="check-circle-bold" />}
                    >
                      {_t("mark", "paid")} ({format.price(invoice.totals?.withVat, 2)})
                    </Button>
                  </Popconfirm>
                )}
              </Col>
            </Row>
            <DividerLine className="mt-05 mb-05" />
            <Row justify="end" align="middle">
              <Col>
                <Space size="small" split="•">
                  {/* // NOTE: 2026-05-27 (Nico): Disable this feature because the offerPdf no longer exists inside E-conomic once the receivable has been converted into an invoice. */}
                  {/* <Button size="small" type="text" icon={<Icon name="eye-outline" />} */}
                  {/*   onClick={() => downloadOffer(invoice)} */}
                  {/*   loading={loadingIds.has(getLoadingId(invoice.id, "downloadOffer"))} */}
                  {/* > */}
                  {/*   {_t("see_original", "offer")} */}
                  {/* </Button> */}
                  <Button size="small" type={invoice.status?.id === PAID_ID ? "text" : "link"} icon={<Icon name="download-outline" />}
                    onClick={() => downloadInvoice(invoice)}
                    disabled={!invoice.links.invoicePdf}
                    loading={loadingIds.has(getLoadingId(invoice.id, "downloadInvoice"))}
                  >
                    {_t("download", "invoice")}
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>
        )
      })}
    </div>
  )
}

